import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Calendar, Building } from 'lucide-react';
import { Promotion } from '../../types/promotion';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

interface PromotionCardProps {
  promotion: Promotion;
}

export const PromotionCard: React.FC<PromotionCardProps> = ({ promotion }) => {
  const getStatusBadge = (status: string) => {
    const styles = {
      published: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
      draft: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
      sold_out: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
      archived: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
    };

    const labels = {
      published: 'Publiée',
      draft: 'Brouillon',
      sold_out: 'Épuisée',
      archived: 'Archivée',
    };

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${styles[status as keyof typeof styles]}`}>
        {labels[status as keyof typeof labels]}
      </span>
    );
  };

  const formatDeliveryDate = (date: string) => {
    try {
      return format(new Date(date), 'MMMM yyyy', { locale: fr });
    } catch {
      return date;
    }
  };

  return (
    <Link
      to={`/promotions/${promotion.id}`}
      className="group relative overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-900/5 dark:bg-gray-800 hover:shadow-md transition-all duration-200 hover:scale-[1.02]"
    >
      <div className="aspect-video relative">
        <div className="absolute left-3 top-3 sm:left-2 sm:top-2 z-10">
          {getStatusBadge(promotion.status)}
        </div>
        {promotion.photos && promotion.photos.length > 0 ? (
          <img
            src={promotion.photos[0]}
            alt={promotion.name}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gray-200 dark:bg-gray-700">
            <Building className="h-12 w-12 text-gray-400" />
          </div>
        )}
      </div>

      <div className="p-4 sm:p-3">
        <div className="flex items-start justify-between">
          <h3 className="text-base sm:text-sm font-medium text-gray-900 dark:text-white truncate">
            {promotion.name}
          </h3>
        </div>

        <div className="mt-2 sm:mt-1 flex items-center text-sm sm:text-xs text-gray-600 dark:text-gray-400">
          <MapPin className="h-4 w-4 sm:h-3 sm:w-3 mr-1" />
          <span className="truncate">{promotion.city}</span>
        </div>

        {promotion.delivery_date && (
          <div className="mt-2 sm:mt-1 flex items-center text-sm sm:text-xs text-gray-600 dark:text-gray-400">
            <Calendar className="h-4 w-4 sm:h-3 sm:w-3 mr-1" />
            <span>Livraison : {formatDeliveryDate(promotion.delivery_date)}</span>
          </div>
        )}

        <div className="mt-3 sm:mt-2 flex gap-4 text-sm sm:text-xs text-gray-600 dark:text-gray-400">
          <span>{promotion.total_units} lots</span>
          <span>{promotion.total_blocks} blocs</span>
          <div className="ml-auto flex items-center gap-2">
            <div className="flex items-center" title="Disponibles">
              <span className="w-2 h-2 rounded-full bg-green-500 mr-1"></span>
              <span>{promotion.available_units}</span>
            </div>
            <div className="flex items-center" title="Réservés">
              <span className="w-2 h-2 rounded-full bg-orange-500 mr-1"></span>
              <span>{promotion.reserved_units}</span>
            </div>
            <div className="flex items-center" title="Vendus">
              <span className="w-2 h-2 rounded-full bg-blue-500 mr-1"></span>
              <span>{promotion.sold_units}</span>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};
